/**
 * Upload orchestrator for document media (encrypted inline images), now backed
 * by the PERSISTENT OUTBOX (services/documentAttachmentOutbox.js).
 *
 * The plugin (components/ui/DocMediaPlugin.js) stages an ALREADY-ENCRYPTED
 * blob here and inserts a pending DocImageNode carrying the returned
 * `pendingRef`; this module owns everything after that moment:
 *
 *  - PERSIST-FIRST staging: `stagePending` writes meta + ciphertext to the
 *    outbox BEFORE resolving the pendingRef, so the node the plugin inserts
 *    always refers to bytes that survive a crash/restart. (The inverse orphan
 *    — a crash after enqueue but before the node insert — leaves an outbox
 *    item whose upload later finds no node to rewrite and is removed then;
 *    see the outbox header for the deliberate no-GC v1 lifecycle.) An outbox
 *    write FAILURE degrades to chunk 8's in-memory-only behavior rather than
 *    blocking the paste.
 *  - a SERIAL upload queue (one POST at a time — uploads share the user's
 *    uplink), fed by `stagePending` and, after `startDocumentAttachmentUploader`,
 *    by the outbox's persisted backlog (`resume`).
 *  - the AUTH GATE: every upload requires a signed-in session, so the queue
 *    idles while signed out. `personal:local` items additionally wait for the
 *    sign-in retag (`retagScope(personal:local -> personal:<userId>)`, the
 *    exact mirror of how `personal_migrate_local_scope` folds signed-out
 *    document drafts into the user's scope — including that they fold into
 *    WHICHEVER account signs in first). `personal:<otherUser>` items wait for
 *    that user; `team:*` items upload for any signed-in session and the
 *    server authorizes membership.
 *  - AUTO-RETRY with backoff for transient failures: 30s -> 2m -> 10m -> 60m
 *    (capped), timer per item, cleaned up on manual retry/removal. HTTP 413
 *    and 422 are PERMANENT: the entry parks in 'error' with `lastError` and
 *    only the user's explicit Retry re-queues it. A window 'online' event
 *    fires all pending auto-retries immediately.
 *  - a per-ref status store ('uploading' | 'error' | 'done') shaped for
 *    `useSyncExternalStore` — unchanged from chunk 8, which is how
 *    DocImageComponent renders without prop-drilling through Lexical.
 *  - RESULT DELIVERY. Live document open (registerLiveRewriter): the rewriter
 *    gets `(pendingRef, url)` and flips the node inside a normal editor
 *    update; the stored body is deliberately NOT touched — the live editor
 *    owns the document, and a concurrent store write could clobber newer
 *    typed content. No live editor: the CLOSED-DOC REWRITE patches the
 *    freshest stored body directly (bodyLexical node tree + bodyHtml img,
 *    sanitized) and invalidates the docBodyCache entry so a reopen cannot
 *    resurrect the pre-swap body. A rewriter appearing MID-rewrite aborts the
 *    store write and delivers live instead. A completed upload whose rewrite
 *    could not land ('uploaded-unwritten' — store save failed, or the doc row
 *    is not visible yet, e.g. the sign-in drafts migration racing us) is
 *    retried rewrite-only: on the doc's next registerLiveRewriter this
 *    session, or by `resume` on the next launch — never re-uploaded.
 *
 * `uploadDocumentAttachment` remains the bare network call. Registry entries
 * are session-scoped; real lifecycle (crash survival, retry across restarts)
 * lives in the outbox, whose items are removed only once delivered/rewritten.
 */
import { apiClient } from '../utils/apiClient';
import { useAuthStore } from '../store/authStore';
import * as outbox from './documentAttachmentOutbox';
import { PERSONAL_LOCAL_SCOPE } from './personalScope';
import {
  readDocument,
  saveDocumentBody,
  recordsContext,
} from '../extensions/bundled/documents/documentsStore';
import { docBodyCache, docBodyCacheKey } from '../extensions/bundled/documents/docBodyCache';
import { isDocDeleted } from '../extensions/bundled/documents/deletedDocsGuard';
import { sanitizeRichTextHtml } from '../utils/sanitizeHtml';
import { cloudPlanRefusal } from '../plans/cloudState';

// The global apiClient timeout is 10s — tuned for JSON round trips, lethal
// for a 25MB upload on a slow uplink. Per-request override; the Tauri Rust
// adapter ignores timeouts entirely (no cap in the packaged app), so this
// governs the browser/dev path, where axios WOULD abort at 10s.
export const UPLOAD_TIMEOUT_MS = 120000;

// Auto-retry backoff schedule for TRANSIENT upload failures: attempt n waits
// BACKOFF_DELAYS_MS[n-1], capped at the last entry. Manual Retry bypasses it.
export const BACKOFF_DELAYS_MS = [30_000, 120_000, 600_000, 3_600_000];

// Responses that retrying can never fix: 413 (too large) and 422 (rejected
// payload). These park permanently in 'error' — user-driven retry only.
const PERMANENT_UPLOAD_STATUSES = new Set([413, 422]);

/**
 * Seam for pendingRef minting: crypto.randomUUID where the runtime has it
 * (every real webview), with a getRandomValues fallback (jsdom's older
 * webcrypto lacks randomUUID). Output always satisfies the node schema's
 * DOC_MEDIA_PENDING_REF_RE ([A-Za-z0-9_-]{6,64}).
 */
const PENDING_REF_RE = /^[A-Za-z0-9_-]{6,64}$/;

export function mintPendingRef() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes =
    typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function'
      ? crypto.getRandomValues(new Uint8Array(16))
      : Uint8Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
  let s = '';
  for (let i = 0; i < bytes.length; i += 1) s += bytes[i].toString(16).padStart(2, '0');
  return s;
}

/** {type:'personal'} | {type:'team', teamId} -> the endpoint's scope params. */
function scopeParams(scope) {
  if (scope && scope.type === 'team' && scope.teamId != null) {
    return { scope_type: 'team', team_id: String(scope.teamId) };
  }
  return { scope_type: 'personal' };
}

/**
 * The bare upload: POST the ciphertext to /documents/attachments as multipart
 * form data, exactly the backend controller's contract (file/.bin +
 * name/mime/size metadata + doc_uid + scope). Resolves the server's
 * `{ url, name, size, mime, kind, enc }` meta. No registry involvement.
 */
export async function uploadDocumentAttachment({ scope, docId, name, mime, size }, ciphertextBlob) {
  const form = new FormData();
  // The stored object is opaque ciphertext, named so nothing ever tries to
  // sniff it as an image (same convention as chat attachment uploads).
  form.append('file', new File([ciphertextBlob], `${name}.bin`, { type: 'application/octet-stream' }));
  form.append('name', name);
  form.append('mime', mime);
  form.append('size', String(size));
  form.append('doc_uid', docId);
  const params = scopeParams(scope);
  form.append('scope_type', params.scope_type);
  if (params.team_id != null) form.append('team_id', params.team_id);
  const res = await apiClient.post('/documents/attachments', form, {
    // Same explicit header as chatService.uploadAttachment: the browser XHR
    // path replaces it with the boundary-carrying version, the Tauri adapter
    // strips it and lets reqwest set its own.
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: UPLOAD_TIMEOUT_MS,
  });
  return res.data; // { url, name, size, mime, kind, enc }
}

// pendingRef -> { meta (outbox meta shape), previewObjectUrl, ciphertextBlob,
//                 status: 'uploading'|'error'|'done', url, finalized }
// `finalized` = the result reached its document (live delivery or store
// rewrite) and the outbox item is gone; guards against double delivery.
const registry = new Map();
const listeners = new Set();
const queue = []; // pendingRefs awaiting upload, FIFO among eligible items
let activeRef = null; // the ref whose POST is in flight (serialization token)
const rewriters = new Map(); // docId -> fn(pendingRef, url)
const retryTimers = new Map(); // pendingRef -> auto-retry timeout id

let uploaderStarted = false;
let unsubscribeAuth = null;
// Set the moment a sign-in retag is decided, BEFORE any await — so a resume()
// racing the retag maps personal:local items it registers to the new key
// instead of parking them on a bucket that no longer exists.
let personalLocalRetagKey = null;
let retagChain = Promise.resolve();

function notify() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      console.warn('[docs] attachment listener failed:', err?.message || err);
    }
  });
}

/** useSyncExternalStore subscribe — fires on every status transition. */
export function subscribeUploads(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * 'uploading' | 'error' | 'done' for a ref this session knows (staged here or
 * re-registered from the outbox by `resume`), or null for a foreign ref — a
 * pending node from another device, whose outbox this one never held.
 * Value-stable per state, so it doubles as the useSyncExternalStore snapshot.
 */
export function getUploadStatus(pendingRef) {
  return registry.get(pendingRef)?.status ?? null;
}

/**
 * Why the last upload attempt failed, for the error ribbon: 'HTTP <status>',
 * 'timeout', or 'network'. Null unless the entry is in 'error'. Set before
 * the status notify fires, so a subscriber re-rendering on the transition
 * always reads a consistent pair.
 */
export function getUploadError(pendingRef) {
  const entry = registry.get(pendingRef);
  return entry?.status === 'error' ? (entry.errorDetail ?? null) : null;
}

/**
 * The staged compressed-plaintext preview object URL, or null. Session-only —
 * a resumed item has no preview here; DocImageComponent falls back to
 * decrypting the outbox ciphertext itself.
 */
export function getPreview(pendingRef) {
  return registry.get(pendingRef)?.previewObjectUrl ?? null;
}

/** True while a live editor for docId is registered (its rewriter owns delivery). */
function hasLiveRewriter(docId) {
  return rewriters.has(docId);
}

/**
 * The live editor for `docId` announces itself: `fn(pendingRef, url)` is
 * called for every upload of that document that completes while registered —
 * plus, immediately, any COMPLETED-BUT-UNWRITTEN result already held for it
 * (upload landed while the tab was closed and the store rewrite has not
 * finalized: the persistent successor of chunk 8's in-memory hold). One
 * rewriter per docId (an editor instance); returns the unregister.
 */
export function registerLiveRewriter(docId, fn) {
  rewriters.set(docId, fn);
  registry.forEach((entry, pendingRef) => {
    if (entry.meta.docId !== docId || entry.status !== 'done' || entry.finalized || !entry.url) {
      return;
    }
    entry.finalized = true; // claim before delivering — finalizeUploaded checks this
    try {
      fn(pendingRef, entry.url);
    } catch (err) {
      console.warn('[docs] live rewriter failed:', err?.message || err);
    }
    outbox.remove(pendingRef).catch(() => {});
  });
  return () => {
    if (rewriters.get(docId) === fn) rewriters.delete(docId);
  };
}

/* ── eligibility (the auth gate) ─────────────────────────────────────────── */

/**
 * Every upload endpoint requires auth, so nothing moves while signed out.
 * personal:local waits for the sign-in retag; a personal item tagged for a
 * DIFFERENT user waits for that user (shared-device case); team items rely on
 * the server's membership check.
 */
function isEligible(meta) {
  const { isAuthenticated, user } = useAuthStore.getState();
  if (!isAuthenticated || user?.id == null) return false;
  const key = meta.scopeKey || '';
  if (key === PERSONAL_LOCAL_SCOPE) return false;
  if (key.startsWith('personal:')) return key === `personal:${user.id}`;
  return true;
}

/* ── the serial queue ────────────────────────────────────────────────────── */

function pump() {
  if (activeRef != null) return;
  // Drop refs whose registry entry vanished, then take the FIRST eligible one;
  // ineligible items stay queued for the next trigger (sign-in, retag, online).
  for (let i = queue.length - 1; i >= 0; i -= 1) {
    if (!registry.has(queue[i])) queue.splice(i, 1);
  }
  const idx = queue.findIndex((ref) => isEligible(registry.get(ref).meta));
  if (idx === -1) return;
  const ref = queue.splice(idx, 1)[0];
  const entry = registry.get(ref);
  activeRef = ref;
  runUpload(ref, entry).finally(() => {
    activeRef = null;
    pump();
  });
}

async function runUpload(ref, entry) {
  try {
    // The doc was deleted IN THIS SESSION (deletedDocsGuard): uploading would
    // orphan a server object nothing can ever reference, and the rewrite
    // could never land. Drop the item instead — review finding: without this,
    // a deleted doc's queued/error items re-POSTed on every resume, forever.
    if (isDocDeleted(entry.meta.docId)) {
      await finalizeDeletedDoc(ref, entry);
      return;
    }
    entry.meta.state = 'uploading';
    await outbox.updateMeta(ref, { state: 'uploading' }).catch(() => {});
    let ciphertextBlob = entry.ciphertextBlob;
    if (!ciphertextBlob) ciphertextBlob = await outbox.readCiphertext(ref);
    if (!ciphertextBlob) {
      // Bytes unrecoverable (corrupted outbox): this item can never upload.
      // Drop it — the node renders the inert waiting box, same as a foreign
      // device's ref.
      console.warn('[docs] outbox item has no ciphertext — dropping:', ref);
      await outbox.remove(ref).catch(() => {});
      registry.delete(ref);
      notify();
      return;
    }
    const { docId, scope, name, mime, size } = entry.meta;
    const meta = await uploadDocumentAttachment({ scope, docId, name, mime, size }, ciphertextBlob);
    const url = meta?.url ?? null;
    if (!url) throw new Error('upload response carried no url');
    entry.status = 'done';
    entry.url = url;
    entry.ciphertextBlob = null; // upload landed — free the big buffer
    entry.meta.state = 'uploaded-unwritten';
    entry.meta.url = url;
    // Persist success BEFORE delivering: a crash after this line re-runs only
    // the rewrite (resume's 'uploaded-unwritten' path), never the POST.
    await outbox.updateMeta(ref, { state: 'uploaded-unwritten', url }).catch(() => {});
    notify();
    try {
      await finalizeUploaded(ref, entry);
    } catch (finalizeErr) {
      // The upload itself SUCCEEDED — a delivery bug must never park the
      // entry in 'error' (that path re-POSTs, duplicating the object). The
      // item stays 'uploaded-unwritten' for a later rewrite-only retry.
      console.warn('[docs] attachment delivery failed:', finalizeErr?.message || finalizeErr);
    }
  } catch (err) {
    const status = err?.response?.status;
    // Plans v2: a 402 cloud plan refusal (read-only / no cloud) cannot change
    // until the plan does — no auto-retry loop; Retry stays manual.
    const planRefusal = status === 402 ? cloudPlanRefusal(err?.response?.data) : null;
    const permanent = PERMANENT_UPLOAD_STATUSES.has(status) || Boolean(planRefusal);
    console.warn('[docs] attachment upload failed:', err?.message || err);
    entry.status = 'error'; // ciphertext kept — Retry re-posts it
    // A human-readable WHY for the ribbon. "Upload failed" alone sent a real
    // bug report on a wild goose chase — a 404 (server without the endpoint),
    // a 503 (storage unconfigured) and a dead network all looked identical.
    entry.errorDetail = planRefusal
      ? planRefusal.error || `HTTP ${status}`
      : status
      ? `HTTP ${status}`
      : err?.code === 'ECONNABORTED'
        ? 'timeout'
        : 'network';
    entry.meta.state = 'error';
    entry.meta.attempts = (entry.meta.attempts || 0) + 1;
    entry.meta.lastError = String(err?.message || err);
    await outbox
      .updateMeta(ref, {
        state: 'error',
        attempts: entry.meta.attempts,
        lastError: entry.meta.lastError,
      })
      .catch(() => {});
    notify();
    if (!permanent) scheduleAutoRetry(ref, entry.meta.attempts);
  }
}

/* ── retry (auto with backoff, manual immediate) ─────────────────────────── */

function scheduleAutoRetry(ref, attempts) {
  clearAutoRetry(ref);
  const delay = BACKOFF_DELAYS_MS[Math.min(Math.max(attempts, 1) - 1, BACKOFF_DELAYS_MS.length - 1)];
  const timer = setTimeout(() => {
    retryTimers.delete(ref);
    requeue(ref);
  }, delay);
  retryTimers.set(ref, timer);
}

function clearAutoRetry(ref) {
  const timer = retryTimers.get(ref);
  if (timer != null) {
    clearTimeout(timer);
    retryTimers.delete(ref);
  }
}

function requeue(ref) {
  const entry = registry.get(ref);
  if (!entry || entry.status !== 'error') return;
  entry.status = 'uploading';
  notify();
  queue.push(ref);
  pump();
}

/**
 * User-driven retry of a failed upload (no-op for any other state). Bypasses
 * whatever backoff wait is pending — and is the ONLY way a permanent
 * (413/422) failure moves again.
 */
export function retryUpload(pendingRef) {
  const entry = registry.get(pendingRef);
  if (!entry || entry.status !== 'error') return;
  clearAutoRetry(pendingRef);
  requeue(pendingRef);
}

/* ── result delivery: live rewriter or closed-doc store rewrite ──────────── */

/**
 * Recursively patch a Lexical node JSON tree: the doc-image node whose
 * pendingRef matches flips to its uploaded shape (src set, pendingRef
 * cleared — the JSON mirror of DocImageNode.setUploaded). Returns whether
 * anything matched.
 */
function patchLexicalTree(node, pendingRef, url) {
  if (!node || typeof node !== 'object') return false;
  let found = false;
  if (node.type === 'doc-image' && node.pendingRef === pendingRef) {
    node.src = url;
    node.pendingRef = '';
    found = true;
  }
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      if (patchLexicalTree(child, pendingRef, url)) found = true;
    }
  }
  return found;
}

/**
 * Patch the STORED body of a closed document:
 * freshest readDocument -> flip the matching doc-image node in every
 * bodyLexical block tree + the matching img in bodyHtml (src set,
 * data-pending-ref dropped, re-serialized through sanitizeRichTextHtml) ->
 * saveDocumentBody -> invalidate the docBodyCache entry.
 *
 * Returns 'saved' | 'node-gone' | 'doc-missing' | 'rewriter-appeared' |
 * 'failed'. 'doc-missing' deliberately does NOT count as node-gone: during
 * the sign-in fold the doc row may simply not have migrated into the user's
 * bucket yet, and removing the item there would lose the image — the item
 * stays 'uploaded-unwritten' and the rewrite retries later.
 */
async function rewriteStoredDocument(pendingRef, entry) {
  const { docId, scope } = entry.meta;
  const url = entry.url;
  let doc;
  try {
    doc = await readDocument(docId, scope);
  } catch (err) {
    console.warn('[docs] closed-doc rewrite: read failed:', err?.message || err);
    return 'failed';
  }
  if (!doc) {
    // Locally deleted is DISTINGUISHABLE from not-yet-migrated: the guard
    // marks every session-local delete before its first tombstone write. A
    // deleted doc's item must be dropped (retaining it leaked meta + up to
    // 25MB of ciphertext per image, re-probed every launch); a merely
    // missing doc (mid sign-in fold) still retries later.
    if (isDocDeleted(docId)) return 'doc-deleted';
    return 'doc-missing';
  }

  let found = false;
  const bodyLexical = doc.bodyLexical || null;
  if (bodyLexical && Array.isArray(bodyLexical.blocks)) {
    for (const block of bodyLexical.blocks) {
      if (block && patchLexicalTree(block.node, pendingRef, url)) found = true;
    }
  }

  let bodyHtml = doc.bodyHtml || '';
  if (bodyHtml) {
    const parsed = new DOMParser().parseFromString(bodyHtml, 'text/html');
    // pendingRefs match [A-Za-z0-9_-] (validated by the node schema), so the
    // ref is safe inside a quoted attribute selector.
    const img = parsed.querySelector(`img[data-pending-ref="${pendingRef}"]`);
    if (img) {
      img.setAttribute('src', url);
      img.removeAttribute('data-pending-ref');
      bodyHtml = sanitizeRichTextHtml(parsed.body.innerHTML);
      found = true;
    }
  }

  if (!found) return 'node-gone';
  // The document was (re)opened while we were reading — the live editor owns
  // it now; writing the store here could clobber newer typed content.
  if (hasLiveRewriter(docId)) return 'rewriter-appeared';
  try {
    await saveDocumentBody(docId, { bodyHtml, bodyLexical }, scope);
  } catch (err) {
    console.warn('[docs] closed-doc rewrite: save failed:', err?.message || err);
    return 'failed';
  }
  try {
    // Without this, reopening the doc would serve the cached PRE-swap body
    // and the next autosave would write the pending node back over the fix.
    docBodyCache.delete(docBodyCacheKey(docId, scope));
  } catch (err) {
    console.warn('[docs] closed-doc rewrite: cache invalidation failed:', err?.message || err);
  }
  return 'saved';
}

/**
 * Route a completed upload to its document. Live rewriter registered -> live
 * delivery, store untouched. Otherwise the closed-doc rewrite; a rewriter
 * appearing mid-rewrite loops back to live delivery (bounded — each loop
 * needs an actual registration flip). 'delivered/saved/node-gone/doc-deleted'
 * remove the outbox item; 'doc-missing'/'failed' leave it
 * 'uploaded-unwritten' for a later rewrite-only retry. ('doc-deleted' means
 * the deletedDocsGuard knows this doc was deleted locally — the already-
 * uploaded object becomes orphaned on the server and is reclaimed when the
 * doc's tombstone purges, per the backend GC.)
 */
async function finalizeUploaded(pendingRef, entry) {
  for (let round = 0; round < 3; round += 1) {
    if (entry.finalized) return; // a registerLiveRewriter scan claimed it
    const rewriter = rewriters.get(entry.meta.docId);
    if (rewriter) {
      entry.finalized = true;
      try {
        rewriter(pendingRef, entry.url);
      } catch (err) {
        console.warn('[docs] live rewriter failed:', err?.message || err);
      }
      await outbox.remove(pendingRef).catch(() => {});
      return;
    }
    const result = await rewriteStoredDocument(pendingRef, entry);
    if (entry.finalized) return;
    if (result === 'saved' || result === 'node-gone' || result === 'doc-deleted') {
      entry.finalized = true;
      await outbox.remove(pendingRef).catch(() => {});
      return;
    }
    if (result !== 'rewriter-appeared') return; // 'doc-missing' | 'failed'
  }
}

/** Drop an item whose document was deleted locally: no POST, bytes removed,
 *  status cleared. The pending node (if any copy of it survives anywhere)
 *  renders the inert waiting box. */
async function finalizeDeletedDoc(ref, entry) {
  entry.finalized = true;
  entry.status = 'done';
  registry.delete(ref);
  clearAutoRetry(ref);
  await outbox.remove(ref).catch(() => {});
  notify();
}

/* ── staging (the plugin's entry point) ──────────────────────────────────── */

/**
 * Persists a prepared upload to the outbox, registers it, and starts the
 * queue. `meta` carries everything the eventual POST and the pending UI need:
 * `{ docId, scope, name, mime, size, w, h, keyB64, previewObjectUrl, pendingRef? }`.
 * Resolves the fresh pendingRef the caller writes into its DocImageNode —
 * only AFTER the outbox write settled, so the inserted node never refers to
 * bytes a crash could lose (an outbox FAILURE is degraded to in-memory-only,
 * chunk 8's exact semantics, rather than blocking the paste).
 */
export async function stagePending(meta, ciphertextBlob) {
  // A caller that writes the node BEFORE staging (git sync S2.4b: the body is
  // saved first, so a fast upload always finds its node) brings its own ref.
  const pendingRef = PENDING_REF_RE.test(meta.pendingRef || '') ? meta.pendingRef : mintPendingRef();
  const scope = meta.scope ?? { type: 'personal' };
  let scopeKey;
  try {
    scopeKey = recordsContext(scope).workspaceId;
  } catch {
    scopeKey = PERSONAL_LOCAL_SCOPE;
  }
  if (scopeKey === PERSONAL_LOCAL_SCOPE && personalLocalRetagKey) {
    scopeKey = personalLocalRetagKey; // staged mid-sign-in — tag it directly
  }
  const outboxMeta = {
    id: pendingRef,
    docId: meta.docId,
    scopeKey,
    scope,
    name: meta.name,
    mime: meta.mime,
    size: meta.size,
    w: meta.w ?? null,
    h: meta.h ?? null,
    keyB64: meta.keyB64,
    state: 'queued',
    attempts: 0,
    lastError: null,
    url: null,
    createdAtMs: Date.now(),
  };
  try {
    await outbox.enqueue(outboxMeta, ciphertextBlob);
  } catch (err) {
    console.warn(
      '[docs] outbox persist failed — upload continues in-memory only:',
      err?.message || err
    );
  }
  registry.set(pendingRef, {
    meta: outboxMeta,
    previewObjectUrl: meta.previewObjectUrl ?? null,
    ciphertextBlob,
    status: 'uploading',
    url: null,
    finalized: false,
  });
  notify();
  queue.push(pendingRef);
  pump();
  return pendingRef;
}

/* ── startup resume + triggers ───────────────────────────────────────────── */

/**
 * Re-arm the persisted backlog: 'queued'/'uploading'/'error' items re-enter
 * the upload queue ('uploading' means a crash cut the POST mid-flight —
 * re-posting is the honest recovery); 'uploaded-unwritten' items go STRAIGHT
 * to the rewrite — their url is already in the meta and re-uploading would
 * duplicate the object.
 */
async function resume() {
  let metas;
  try {
    metas = await outbox.list();
  } catch (err) {
    console.warn('[docs] outbox resume failed:', err?.message || err);
    return;
  }
  metas.sort((a, b) => (a?.createdAtMs || 0) - (b?.createdAtMs || 0));
  const rewrites = [];
  for (const meta of metas) {
    if (!meta || typeof meta.id !== 'string' || registry.has(meta.id)) continue;
    if (meta.docId && isDocDeleted(meta.docId)) {
      // Deleted this session while the item waited (offline queue, backoff):
      // never upload or rewrite for it — drop the bytes.
      outbox.remove(meta.id).catch(() => {});
      continue;
    }
    if (meta.scopeKey === PERSONAL_LOCAL_SCOPE && personalLocalRetagKey) {
      meta.scopeKey = personalLocalRetagKey; // listed pre-retag — see handleSignIn
    }
    if (meta.state === 'uploaded-unwritten' && meta.url) {
      const entry = {
        meta,
        previewObjectUrl: null,
        ciphertextBlob: null,
        status: 'done',
        url: meta.url,
        finalized: false,
      };
      registry.set(meta.id, entry);
      rewrites.push([meta.id, entry]);
    } else {
      registry.set(meta.id, {
        meta: { ...meta, state: 'queued' },
        previewObjectUrl: null,
        ciphertextBlob: null, // loaded from the outbox at POST time
        status: 'uploading',
        url: null,
        finalized: false,
      });
      queue.push(meta.id);
    }
  }
  if (metas.length) notify();
  pump();
  for (const [ref, entry] of rewrites) {
    try {
      await finalizeUploaded(ref, entry);
    } catch (err) {
      // One broken rewrite must not starve the rest of the backlog.
      console.warn('[docs] resumed rewrite failed:', err?.message || err);
    }
  }
}

/** Reconnect: fire every pending auto-retry NOW instead of waiting out its backoff. */
function onOnline() {
  const refs = [...retryTimers.keys()];
  refs.forEach((ref) => {
    clearAutoRetry(ref);
    requeue(ref);
  });
  pump();
}

/**
 * Sign-in: fold signed-out staged items into this user's scope
 * (`retagScope(personal:local -> personal:<userId>)`) and kick the queue —
 * the outbox mirror of `personal_migrate_local_scope`. Serialized so a
 * sign-out/sign-in flurry cannot interleave retags.
 */
function handleSignIn(userId) {
  const toKey = `personal:${userId}`;
  personalLocalRetagKey = toKey; // visible to resume()/stagePending immediately
  retagChain = retagChain.then(async () => {
    try {
      await outbox.retagScope(PERSONAL_LOCAL_SCOPE, toKey);
    } catch (err) {
      console.warn('[docs] outbox retag failed:', err?.message || err);
    }
    registry.forEach((entry) => {
      if (entry.meta.scopeKey === PERSONAL_LOCAL_SCOPE) entry.meta.scopeKey = toKey;
    });
    pump();
  });
  return retagChain;
}

const authUserId = (state) =>
  state?.isAuthenticated && state?.user?.id != null ? String(state.user.id) : null;

/**
 * Idempotent init, called once from app bootstrap (src/index.js): resumes the
 * persisted backlog, watches connectivity, and observes the auth store for
 * sign-in (the same signal ensurePersonalSync acts on) to run the retag.
 */
export function startDocumentAttachmentUploader() {
  if (uploaderStarted) return;
  uploaderStarted = true;
  const initialUid = authUserId(useAuthStore.getState());
  if (initialUid) handleSignIn(initialUid); // signed in from a previous session
  resume().catch((err) => console.warn('[docs] outbox resume failed:', err?.message || err));
  if (typeof window !== 'undefined') window.addEventListener('online', onOnline);
  let prevUid = initialUid;
  unsubscribeAuth = useAuthStore.subscribe((state) => {
    const uid = authUserId(state);
    const was = prevUid;
    prevUid = uid;
    if (uid && uid !== was) handleSignIn(uid);
  });
}

/** Test seam: tear down listeners/timers and forget all module state. */
export function __resetDocumentAttachmentUploaderForTests() {
  retryTimers.forEach((timer) => clearTimeout(timer));
  retryTimers.clear();
  if (typeof window !== 'undefined') window.removeEventListener('online', onOnline);
  if (unsubscribeAuth) {
    unsubscribeAuth();
    unsubscribeAuth = null;
  }
  uploaderStarted = false;
  personalLocalRetagKey = null;
  retagChain = Promise.resolve();
  registry.clear();
  listeners.clear();
  queue.length = 0;
  activeRef = null;
  rewriters.clear();
}
